import { useEffect } from 'react'
import { useChatStore } from '@/stores/chat-store'
import { IS_MAC } from '@/features/shortcuts/platform'
import { isAiChatShortcut, isBlockedTarget, nextChatAction } from './ai-chat-shortcut'

/**
 * Mod+K opens Edge AI with the cursor in the box, moves the cursor back into
 * it when the chat is open elsewhere, and closes it from inside the chat.
 * Mounted once, in CompanionHost. Not `useHotkey`: that hook prevents the
 * default before its handler can decline (a modal open must keep the key).
 */
export function useAiChatShortcut(apple: boolean = IS_MAC): void {
  useEffect(() => {
    let returnFocus: HTMLElement | null = null
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !isAiChatShortcut(e, apple) || isBlockedTarget(e.target)) return
      const chat = useChatStore.getState()
      // keydown always targets the focused element, so e.target is "where the cursor is".
      const target = e.target
      const focusInChat =
        target instanceof Element && target.closest('[data-companion-surface]') !== null
      const action = nextChatAction({ open: chat.open, focusInChat })
      e.preventDefault()
      if (action === 'close') {
        chat.setOpen(false)
        if (returnFocus?.isConnected) returnFocus.focus()
        returnFocus = null
        return
      }
      // 'open' or 'focus': remember where the user was, so the closing press returns there.
      if (target instanceof HTMLElement && target !== document.body) returnFocus = target
      chat.focusComposer()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [apple])
}
